import React, { useEffect, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

// Components
import { Card, Alert, Pagination } from 'react-bootstrap';
import AuditFilters from './AuditFilters';
import AuditTable from './AuditTable';

// Helpers
import ApiService from '../../helpers/apiService';

// Types
import { IAuditLog, IAuditFilter } from './types';

interface IAuditResponse {
  code: string;
  data: {
    items: IAuditLog[];
    total: number;
    page: number;
    size: number;
  };
}

const AuditPage = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IAuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const fetchAuditLogs = useCallback(async (filters: IAuditFilter) => {
    setLoading(true);
    setError(null);
    try {
      // Clean up undefined values from params
      const params: Record<string, any> = {
        page: filters.page,
        size: filters.size
      };
      if (filters.action) params.action = filters.action;
      if (filters.resource) params.resource = filters.resource;
      if (filters.profile_id) params.profile_id = filters.profile_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const response = await ApiService.get<IAuditResponse>('/audit/v1/list', {
        params
      });
      setData(response.data.items);
      setTotal(response.data.total);
    } catch (err: any) {
      console.error('Ошибка при загрузке журнала аудита:', err);
      const isNetworkError = err?.message === 'Network Error' || !err?.response;
      const msg = isNetworkError
        ? 'Сервер недоступен. Проверьте, что API запущен и нет блокировки CORS.'
        : (err?.response?.data?.message ?? err?.message) ||
          'Не удалось загрузить журнал аудита';
      const errorMsg =
        typeof msg === 'string' ? msg : 'Ошибка сервера. Попробуйте позже.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  const { handleSubmit, setFieldValue, values } = useFormik<IAuditFilter>({
    initialValues: {
      page: 1,
      size: 20,
      action: undefined,
      resource: undefined,
      date_from: undefined,
      date_to: undefined
    },
    onSubmit: (vals: IAuditFilter) => {
      // Reset to page 1 when filters change via submit
      const filtersWithFirstPage = { ...vals, page: 1 };
      setFieldValue('page', 1);
      fetchAuditLogs(filtersWithFirstPage);
    }
  });

  // Initial load
  useEffect(() => {
    fetchAuditLogs(values);
  }, []);

  // Pagination
  const totalPages = Math.ceil(total / values.size);

  const handlePageChange = async (page: number) => {
    if (page < 1 || page > totalPages || page === values.page) return;
    await setFieldValue('page', page);
    fetchAuditLogs({ ...values, page });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items: React.ReactNode[] = [];
    const maxVisible = 5;
    let startPage = Math.max(1, values.page - Math.floor(maxVisible / 2));
    const endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.First
        key="first"
        disabled={values.page === 1}
        onClick={() => handlePageChange(1)}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        disabled={values.page === 1}
        onClick={() => handlePageChange(values.page - 1)}
      />
    );

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === values.page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={values.page === totalPages}
        onClick={() => handlePageChange(values.page + 1)}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        disabled={values.page === totalPages}
        onClick={() => handlePageChange(totalPages)}
      />
    );

    return (
      <div className="d-flex justify-content-between align-items-center">
        <small className="text-muted">
          Всего записей: {total} | Страница {values.page} из {totalPages}
        </small>
        <Pagination className="mb-0">{items}</Pagination>
      </div>
    );
  };

  return (
    <Card>
      <Card.Header>
        <AuditFilters
          values={values}
          handleSubmit={handleSubmit}
          setFieldValue={setFieldValue}
          loading={loading}
        />
      </Card.Header>
      <Card.Body className="p-0">
        {error && (
          <Alert variant="danger" className="m-3" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <AuditTable data={data} />
      </Card.Body>
      {totalPages > 1 && (
        <Card.Footer>{renderPagination()}</Card.Footer>
      )}
    </Card>
  );
};

export default AuditPage;
